import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Box, Stack } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { getWeeklyHeatMapStats } from '../../../../store/features/getHeatMapStats';
import ChartTooltip from '../../Home/ChartTooltip';

const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const hours = [
  '12am', '1am', '2am', '3am', '4am', '5am', '6am', '7am', '8am', '9am', '10am', '11am',
  '12pm', '1pm', '2pm', '3pm', '4pm', '5pm', '6pm', '7pm', '8pm', '9pm', '10pm', '11pm'
];

const COLORS = {
  high: '#148DFD',
  medium: '#A0D4FF',
  low: '#D6EDFF',
  veryLow: '#F7F8F9',
};

const getIntensity = (totalActivity) => {
  if (totalActivity === 0) return 'veryLow';
  if (totalActivity >= 1 && totalActivity <= 2) return 'low';
  if (totalActivity >= 3 && totalActivity <= 5) return 'medium';
  if (totalActivity >= 6) return 'high';
  return 'veryLow';
};

const transformDataToMatrix = (apiData) => {
  if (!apiData || !Array.isArray(apiData)) {
    return days.map(() => Array(24).fill('veryLow'));
  }

  const dailyData = {};
  apiData.forEach(item => {
    const dayKey = item.dayName;
    if (!dailyData[dayKey]) {
      dailyData[dayKey] = Array(24).fill(0);
    }
    dailyData[dayKey][item.hour] = item.totalActivity || 0;
  });

  return days.map(day => {
    const dayData = dailyData[day] || Array(24).fill(0);
    return dayData.map(activity => getIntensity(activity));
  });
};




export default function ChatTicketHeatmap({ groupId }) {
  const dispatch = useDispatch();
  const [tooltip, setTooltip] = useState({ visible: false, position: null, data: null });
  
  const { data: weeklyData, loading, error } = useSelector((state) => state.heatMapStats.weeklyData);
  
  const heatmapMatrix = transformDataToMatrix(weeklyData?.data);
  const heatmapData = weeklyData?.data || [];
  
  useEffect(() => {
    if (!groupId) return;
    dispatch(getWeeklyHeatMapStats({ group_id: groupId }));
  }, [dispatch, groupId]);

  // Get data for specific day and hour
  const getDayHourData = (dayIdx, hourIdx) => {
    const dayName = days[dayIdx];
    return heatmapData.find(data => data.dayName === dayName && data.hour === hourIdx) || {
      dayName: dayName,
      hour: hourIdx,
      hourLabel: hours[hourIdx],
      totalChats: 0,
      totalTickets: 0,
      totalActivity: 0
    };
  };

  // Handle mouse enter on heatmap box
  const handleMouseEnter = (event, dayIdx, hourIdx) => {
    const dayHourData = getDayHourData(dayIdx, hourIdx);
    const rect = event.currentTarget.getBoundingClientRect();
    const isLastHour = hourIdx === 23;
    
    setTooltip({
      visible: true,
      position: {
        x: isLastHour ? rect.left - 90 : rect.left + rect.width / 2,
        y: rect.top
      },
      data: {
        title: `Activity`,
        stats: [
          { label: 'Total Chats', value: dayHourData.totalChats },
          { label: 'Total Tickets', value: dayHourData.totalTickets }
        ]
      }
    });
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setTooltip({ visible: false, position: null, data: null });
  };

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        width:'100%'
      }}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Box>
            <Typography
              sx={{
                color: '#061522',
                fontFamily: 'Inter, sans-serif',
                fontSize: '18px',
                fontWeight: 500,
                lineHeight: '24px',
                mb: 0.5,
              }}
            >
              Chat/Ticket Heatmap
            </Typography>
            <Typography
              sx={{
                color: '#405261',
                fontFamily: 'Inter, sans-serif',
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '20px',
              }}
            >
              Peak activity by day and hour
            </Typography>
          </Box>
          
        </Stack>

        <Box sx={{ overflowX: 'auto' }}>
          <Box sx={{ minWidth: 600 }}>
       

            {days.map((day, dayIdx) => (
              <Stack
                key={day}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Box sx={{ width: '50px', flexShrink: 0 }}>
                  <Typography
                    sx={{
                      color: '#405261',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
                {heatmapMatrix[dayIdx].map((intensity, hourIdx) => (
                  <Box
                    key={`${day}-${hourIdx}`}
                    onMouseEnter={(e) => handleMouseEnter(e, dayIdx, hourIdx)}
                    onMouseLeave={handleMouseLeave}
                    sx={{
                      minWidth: { xs: '37px', md: '37px' },
                      height: { xs: '33px', md: '33px' },
                      flex: 1,
                      borderRadius: '8px',
                      bgcolor: COLORS[intensity],
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
                    }}
                  />
                ))}
              </Stack>
            ))}
                 <Stack direction="row" spacing={1} sx={{ mb: 1.5, pl: '60px' }}>
              {hours.map((hour) => (
                <Box
                  key={hour}
                  sx={{
                    minWidth: { xs: '37px', md: '37px' },
                    flex: 1,
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#405261',
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '12px',
                      fontWeight: 400,
                      lineHeight: '16px',
                    }}
                  >
                    {hour}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{ mt: 3 }}
        >
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '16px',
            }}
          >
            Low
          </Typography>
          <Box
            sx={{
              width: '48px',
              height: '8px',
              borderRadius: '4px',
              bgcolor: COLORS.veryLow,
            }}
          />
          <Box
            sx={{
              width: '48px',
              height: '8px',
              borderRadius: '4px',
              bgcolor: COLORS.low,
            }}
          />
          <Box
            sx={{
              width: '48px',
              height: '8px',
              borderRadius: '4px',
              bgcolor: COLORS.medium,
            }}
          />
          <Box
            sx={{
              width: '48px',
              height: '8px',
              borderRadius: '4px',
              bgcolor: COLORS.high,
            }}
          />
          <Typography
            sx={{
              color: '#405261',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              fontWeight: 400,
              lineHeight: '16px',
            }}
          >
            High
          </Typography>
        </Stack>
      </CardContent>
      
      {/* Tooltip */}
      {tooltip.visible && tooltip.data && (
        <ChartTooltip
          title={tooltip.data.title}
          stats={tooltip.data.stats}
          position={tooltip.position}
        />
      )}
    </Card>
  );
}